import { useState } from 'react';

import { type CurrencySymbols } from '@/features/currency/types/currency-types';
import getListings from '@/features/listing/requests/get-listings';
import { PaginationNames } from '@/features/listing/types/listing-filters-types';
import {
  type ListingsCollection,
  type ListingType,
} from '@/features/listing/types/listing-types';
import prepareListingsSearchParams from '@/features/listing/utils/prepare-listings-search-params';
import { type Place } from '@/features/place/types/place-types';
import { getUserBearerCookie } from '@/features/user/cookies/cookie-user-bearer';

export default function useLoadListings(listingType: ListingType, placeBbox: Place['bbox'], currencyName: CurrencySymbols['name']) {
  const searchParams = new URLSearchParams();
  const listingsPerPage = 3;

  const bearerToken = getUserBearerCookie();

  const [loading,
    setLoading] = useState(false);
  const [listings,
    setListings] = useState<ListingsCollection['items']>([]);
  const [hasNextPage,
    setHasNextPage] = useState<boolean>(true);
  const [currentPage,
    setCurrentPage] = useState<number>(1);
  const [error,
    setError] = useState<Error>();

  async function loadMoreListings() {
    setLoading(true);

    try {
      searchParams.set(PaginationNames.PageNumber, String(currentPage));
      searchParams.set(PaginationNames.PageSize, String(listingsPerPage));

      const listingSearchParams = prepareListingsSearchParams(searchParams, listingType, placeBbox);
      const listingsCollection = await getListings(
        listingSearchParams.toString(),
        currencyName,
        bearerToken,
      );
      const totalPages = Math.ceil(listingsCollection.count / listingsPerPage);

      if (currentPage === 1 && listingsCollection.count > 0) {
        listingsCollection.items.shift();
        listingsCollection.count -= 1;
      }

      setCurrentPage(currentPage + 1);
      setListings((current) => [...current, ...listingsCollection.items]);
      setHasNextPage(totalPages !== currentPage);
    } catch (error_) {
      setError(
        error_ instanceof Error ? error_ : new Error('Something went wrong'),
      );
    } finally {
      setLoading(false);
    }
  }

  return {
    error, hasNextPage, listings, loading, loadMoreListings,
  };
}
